import React, { useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { Plus, Pencil, Save, FileSpreadsheet } from "lucide-react";

import {
  db,
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where
} from "../../firebase";

import "./CurriculumPlanPage.css";

const translations = {
  ru: {
    pageTitle: "Учебный план",
    direction: "Направление:",
    academicYear: "Учебный год:",
    addYear: "Добавить год",
    studyForm: "Форма обучения:",
    qualification: "Квалификация:",
    bachelor: "Бакалавр",
    fullTime: "Очная",
    partTime: "Заочная",
    edit: "Редактировать",
    save: "Сохранить",
    addBlock: "+ Раздел",
    addSubblock: "+ Подраздел",
    filterAll: "Все разделы",
    exportExcel: "Экспорт Excel",
    statusReady: "Таблица готова. Для заполнения нажмите «Редактировать».",
    viewMode: "Просмотр",
    editMode: "Редактирование",
    disciplines: "Дисциплины",
    totalLoad: "Общая нагрузка",
    auditorium: "Аудиторные занятия",
    creditsShort: "Кред.",
    hours: "Часы",
    lectureShort: "Лекц.",
    practiceShort: "Практ.",
    labShort: "Лаб.",
    semester1: "1 семестр",
    semester2: "2 семестр",
    semester3: "3 семестр",
    semester4: "4 семестр",
    semester5: "5 семестр",
    semester6: "6 семестр",
    semester7: "7 семестр",
    semester8: "8 семестр",
    blockPlaceholder: "Раздел...",
    subblockPlaceholder: "Подраздел...",
    disciplinePlaceholder: "Дисциплина...",
    totalBlock: "Итого по разделу",
    grandTotal: "ОБЩИЙ ИТОГ:",
    addDisciplineTitle: "Предмет",
    deleteBlockTitle: "Удалить",
    loadYearsError: "Ошибка загрузки учебных годов.",
    promptYear: "Введите новый учебный год. Например: 2027-2028",
    newYearAdded: "Новый год добавлен. Открыта пустая таблица.",
    addYearError: "Ошибка при добавлении года.",
    saved: "Учебный план сохранен.",
    chooseTeacher: "Выбрать...",
    newSubblock: "Подраздел",
    confirmDeleteBlock: "Удалить этот раздел и все дисциплины внутри него?",
    noDataTeacher: "Учебный план для выбранного года не добавлен.",
    noDataAdmin: "Данных нет. Открыта пустая таблица.",
    loaded: "Учебный план загружен.",
    loadError: "Ошибка загрузки.",
    disciplineAdded: "Добавлено.",
    blockAdded: "Добавлено.",
    confirmDeleteSubblock: "Удалить этот подраздел вместе с дисциплинами?",
    blockDeleted: "Удалено.",
    editEnabled: "Режим редактирования включён.",
    exported: "Экспортировано в Excel.",
    sheetName: "Учебный план",
    fileName: "Учебный_план",
    cipher: "Шифр",
    totalGeneral: "Итого",
    ofWhich: "В том числе",
    auditoriumShort: "Аудит.",
    selfStudyShort: "Сам. раб.",
    teacher: "Преподаватель",
    reportingTitle: "Отчетность",
    zachetShort: "Зачет",
    examShort: "Экзамен",
    courseWorkShort: "Курс. раб.",
    weeklyHours: "Число часов в неделю",
    semesterCredits: "Число кредитов в семестре",
    semesterZachets: "Число зачетов в семестре",
    semesterExams: "Число экзаменов в семестре"
  },

  kg: {
    pageTitle: "Окуу планы",
    direction: "Багыт:",
    academicYear: "Окуу жылы:",
    addYear: "Жыл кошуу",
    studyForm: "Окуу формасы:",
    qualification: "Квалификация:",
    bachelor: "Бакалавр",
    fullTime: "Күндүзгү",
    partTime: "Сырттан",
    edit: "Өзгөртүү",
    save: "Сактоо",
    addBlock: "+ Бөлүм",
    addSubblock: "+ Ички бөлүм",
    filterAll: "Баары",
    exportExcel: "Excel экспорт",
    statusReady: "Таблица даяр. Толтуруу үчүн «Өзгөртүү» баскычын басыңыз.",
    viewMode: "Көрүү",
    editMode: "Редактирлөө",
    disciplines: "Дисциплиналар",
    totalLoad: "Жалпы жүктөм",
    auditorium: "Аудиториялык сабактар",
    creditsShort: "Кред.",
    hours: "Саат",
    lectureShort: "Лекц.",
    practiceShort: "Практ.",
    labShort: "Лаб.",
    semester1: "1 семестр",
    semester2: "2 семестр",
    semester3: "3 семестр",
    semester4: "4 семестр",
    semester5: "5 семестр",
    semester6: "6 семестр",
    semester7: "7 семестр",
    semester8: "8 семестр",
    blockPlaceholder: "Бөлүм...",
    subblockPlaceholder: "Ички бөлүм...",
    disciplinePlaceholder: "Дисциплина...",
    totalBlock: "Бөлүм боюнча жыйынтык",
    grandTotal: "ЖАЛПЫ ЖЫЙЫНТЫК:",
    addDisciplineTitle: "Предмет",
    deleteBlockTitle: "Өчүрүү",
    loadYearsError: "Окуу жылдарын жүктөөдө ката.",
    promptYear: "Жаңы окуу жылын жазыңыз. Мисалы: 2027-2028",
    newYearAdded: "Жаңы жыл кошулду. Бош таблица ачылды.",
    addYearError: "Жылды кошууда ката кетти.",
    saved: "Окуу планы сакталды.",
    chooseTeacher: "Тандоо...",
    newSubblock: "Ички бөлүм",
    confirmDeleteBlock: "Бул бөлүмдү жана ичиндеги бардык дисциплиналарды өчүрөсүзбү?",
    noDataTeacher: "Тандалган жыл үчүн окуу планы жок.",
    noDataAdmin: "Маалымат жок. Бош таблица ачылды.",
    loaded: "Окуу планы жүктөлдү.",
    loadError: "Жүктөөдө ката.",
    disciplineAdded: "Кошулду.",
    blockAdded: "Кошулду.",
    confirmDeleteSubblock: "Бул ички бөлүмдү дисциплиналары менен кошо өчүрөсүзбү?",
    blockDeleted: "Өчүрүлдү.",
    editEnabled: "Редактирлөө режими күйдү.",
    exported: "Excel файлына экспорттолду.",
    sheetName: "Окуу планы",
    fileName: "Окуу_планы",
    cipher: "Шифр",
    totalGeneral: "Жалпысы",
    ofWhich: "Анын ичинен",
    auditoriumShort: "Аудиториялык",
    selfStudyShort: "Өз алдынча",
    teacher: "Мугалим",
    reportingTitle: "Отчеттуулук",
    zachetShort: "Зачет",
    examShort: "Сынак",
    courseWorkShort: "Курстук иш",
    weeklyHours: "Жумасы сааттын саны",
    semesterCredits: "Семестрдеги кредиттин саны",
    semesterZachets: "Семестрдеги зачеттордун саны",
    semesterExams: "Семестрдеги сынактардын саны"
  },

  en: {
    pageTitle: "Curriculum Plan",
    direction: "Direction:",
    academicYear: "Academic year:",
    addYear: "Add year",
    studyForm: "Study form:",
    qualification: "Qualification:",
    bachelor: "Bachelor",
    fullTime: "Full-time",
    partTime: "Part-time",
    edit: "Edit",
    save: "Save",
    addBlock: "+ Section",
    addSubblock: "+ Subsection",
    filterAll: "All sections",
    exportExcel: "Export Excel",
    statusReady: "Table is ready. Click «Edit» to fill in.",
    viewMode: "View",
    editMode: "Edit mode",
    disciplines: "Disciplines",
    totalLoad: "Total workload",
    auditorium: "Classroom lessons",
    creditsShort: "Cred.",
    hours: "Hours",
    lectureShort: "Lect.",
    practiceShort: "Pract.",
    labShort: "Lab.",
    semester1: "Semester 1",
    semester2: "Semester 2",
    semester3: "Semester 3",
    semester4: "Semester 4",
    semester5: "Semester 5",
    semester6: "Semester 6",
    semester7: "Semester 7",
    semester8: "Semester 8",
    blockPlaceholder: "Section...",
    subblockPlaceholder: "Subsection...",
    disciplinePlaceholder: "Discipline...",
    totalBlock: "Total for section",
    grandTotal: "GRAND TOTAL:",
    addDisciplineTitle: "Subject",
    deleteBlockTitle: "Delete",
    loadYearsError: "Error loading academic years.",
    promptYear: "Enter new academic year. Example: 2027-2028",
    newYearAdded: "New year added. Empty table opened.",
    addYearError: "Error adding year.",
    saved: "Curriculum plan saved.",
    chooseTeacher: "Choose...",
    newSubblock: "Subsection",
    confirmDeleteBlock: "Delete this section and all its disciplines?",
    noDataTeacher: "No curriculum plan for selected year.",
    noDataAdmin: "No data. Empty table opened.",
    loaded: "Curriculum plan loaded.",
    loadError: "Error loading.",
    disciplineAdded: "Added.",
    blockAdded: "Added.",
    confirmDeleteSubblock: "Delete this subsection with all its disciplines?",
    blockDeleted: "Deleted.",
    editEnabled: "Edit mode enabled.",
    exported: "Exported to Excel.",
    sheetName: "Curriculum plan",
    fileName: "Curriculum_plan",
    cipher: "Cipher",
    totalGeneral: "Total",
    ofWhich: "Of which",
    auditoriumShort: "Classroom",
    selfStudyShort: "Self-study",
    teacher: "Teacher",
    reportingTitle: "Reporting",
    zachetShort: "Pass",
    examShort: "Exam",
    courseWorkShort: "Course work",
    weeklyHours: "Number of hours per week",
    semesterCredits: "Number of credits per semester",
    semesterZachets: "Number of pass tests",
    semesterExams: "Number of exams"
  }
};

const DEFAULT_YEARS = ["2025-2026", "2026-2027"];

const defaultCycleSubblockNames = [
  {
    kg: "ГУМАНИТАРДЫК, СОЦИАЛДЫК ЖАНА ЭКОНОМИКАЛЫК ЦИКЛ",
    ru: "ГУМАНИТАРНЫЙ, СОЦИАЛЬНЫЙ И ЭКОНОМИЧЕСКИЙ ЦИКЛ",
    en: "HUMANITARIAN, SOCIAL AND ECONOMIC CYCLE"
  },
  {
    kg: "Негизги бөлүк",
    ru: "Базовая часть",
    en: "Basic part"
  },
  {
    kg: "Өзгөрмөлүү бөлүк, а.и. студенттердин тандоо курстары",
    ru: "Вариативная часть, в т.ч. курсы по выбору студентов",
    en: "Variable part, including student elective courses"
  },
  {
    kg: "МАТЕМАТИКАЛЫК ЖАНА ТАБИГЫЙ ИЛИМДИК ЦИКЛ",
    ru: "МАТЕМАТИЧЕСКИЙ И ЕСТЕСТВЕННОНАУЧНЫЙ ЦИКЛ",
    en: "MATHEMATICAL AND NATURAL SCIENCE CYCLE"
  },
  {
    kg: "КЕСИПТИК ЦИКЛ / ПРОФЕССИОНАЛЬНЫЙ ЦИКЛ",
    ru: "ПРОФЕССИОНАЛЬНЫЙ ЦИКЛ",
    en: "PROFESSIONAL CYCLE"
  },
  {
    kg: "Практика",
    ru: "Практика",
    en: "Practice"
  },
  {
    kg: "ЖАЛПЫ ИТОГО:",
    ru: "ОБЩИЙ ИТОГ:",
    en: "GRAND TOTAL:"
  }
];

function getTranslatedName(name, lang) {
  if (!name) return "";
  const trimmed = name.trim();
  const match = defaultCycleSubblockNames.find(item => 
    Object.values(item).some(val => val.toLowerCase() === trimmed.toLowerCase())
  );
  if (match) {
    return match[lang] || match.ru || name;
  }
  return name;
}

function makeSafeDocId(value = "") {
  return String(value || "")
    .trim()
    .replaceAll("/", "-")
    .replaceAll("\\", "-")
    .replace(/\s+/g, "_");
}

function makePlanDocId(direction, year, form) {
  return [
    makeSafeDocId(direction),
    makeSafeDocId(year),
    makeSafeDocId(form)
  ].join("__");
}

const planCache = {};

export default function CurriculumPlanPage({
  mode = "admin",
  role = mode,
  language = "ru",
  t: parentT = {},
  readOnly = false,
  readonly = false,
  teacher = {},
  teacherId = ""
}) {
  const rootRef = useRef(null);
  const directionRef = useRef("ИВТ");
  const yearRef      = useRef("2025-2026");
  const studyFormRef = useRef("Очная");

  const normalizedMode = String(mode || "").trim().toLowerCase();
  const normalizedRole = String(role || mode || "").trim().toLowerCase();

  const isTeacher =
    normalizedMode === "teacher" ||
    normalizedRole === "teacher" ||
    normalizedRole === "преподаватель";

  const isLab =
    normalizedMode === "lab" ||
    normalizedMode === "laborant" ||
    normalizedRole === "lab" ||
    normalizedRole === "laborant" ||
    normalizedRole === "лаборант";

  const isReadOnly = Boolean(readOnly || readonly);

  const text = useMemo(() => {
    const base = {
      ...(translations[language] || translations.ru),
      ...parentT
    };
    if (isTeacher) {
      base.pageTitle = base.plans || base.pageTitle;
    }
    return base;
  }, [language, parentT, isTeacher]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const SEMESTERS = [
      text.semester1, text.semester2, text.semester3, text.semester4,
      text.semester5, text.semester6, text.semester7, text.semester8
    ];


    let isEditMode = !isReadOnly;
    let rows       = [];
    let years      = [];
    let currentFilter = "all";
    let teachers   = [];
    let knownDisciplinesData = {};
    let justAddedRowId = null;
    let cyclesWithChildren = new Set();
    let subblocksWithChildren = new Set();

    function isElectiveSubblock(subblockCode) {
      if (!subblockCode) return false;
      const sbRow = rows.find(r => r.type === "subblock" && r.code === subblockCode);
      if (!sbRow) return false;
      const nameLower = String(sbRow.name || "").toLowerCase();
      return nameLower.includes("выбор") || nameLower.includes("тандоочу") || nameLower.includes("электив");
    }

    const actionLabels = {
      ru: { addSub: "Подраздел", addDisc: "Предмет", addCycle: "Раздел", del: "Удалить", actions: "Действия" },
      kg: { addSub: "Ички бөлүм", addDisc: "Предмет", addCycle: "Бөлүм", del: "Өчүрүү", actions: "Аракеттер" },
      en: { addSub: "Subsection", addDisc: "Subject", addCycle: "Section", del: "Delete", actions: "Actions" }
    };
    const act = actionLabels[language] || actionLabels.ru;

    function $(id) { return root.querySelector(`#${id}`); }

  

    function emptySemesters() {
      return Array.from({ length: 8 }, () => ({
        lecture: "", practice: "", lab: "", credit: ""
      }));
    }

    function makeRow(type = "discipline", parentCycleCode = "", code = "", name = "", parentSubblockCode = "") {
      return {
        id: Math.random().toString(36).substr(2, 9),
        type,
        parentCycleCode: parentCycleCode || "",
        parentSubblockCode: parentSubblockCode || "",
        code: code || "",
        name: name || "",
        teacherId: "",
        teacherName: "",
        credits: "", hours: "", lecture: "", practice: "", lab: "",
        semesters: emptySemesters(),
        hasMismatch: false,
        mismatchFields: {},
        zachet: "", exam: "", courseWork: ""
      };
    }

    function defaultRows() {
      return [
        makeRow("cycle",      "", "Б.1", "ГУМАНИТАРДЫК, СОЦИАЛДЫК ЖАНА ЭКОНОМИКАЛЫК ЦИКЛ"),
        makeRow("subblock",   "Б.1", "Б.1.0", "Негизги бөлүк"),
        
        makeRow("subblock",   "Б.1", "Б.1.1.2.0", "Өзгөрмөлүү бөлүк, а.и. студенттердин тандоо курстары"),

        makeRow("cycle",      "", "Б.1.2", "МАТЕМАТИКАЛЫК ЖАНА ТАБИГЫЙ ИЛИМДИК ЦИКЛ"),
        makeRow("subblock",   "Б.1.2", "Б.1.2.0", "Негизги бөлүк"),

        makeRow("cycle",      "", "Б.1.3", "КЕСИПТИК ЦИКЛ / ПРОФЕССИОНАЛЬНЫЙ ЦИКЛ"),
        makeRow("subblock",   "Б.1.3", "Б.1.3.0", "Негизги бөлүк"),

        makeRow("cycle",      "", "Б.2", "Практика"),
        makeRow("subblock",   "Б.2", "Б.2.0", "Негизги бөлүк"),

        makeRow("grand", "", "", text.grandTotal),
      ];
    }

    function normalizeRows(list) {
      if (!Array.isArray(list) || list.length === 0) return defaultRows();

      const hasNewFormat = list.some(r => ["cycle", "subblock"].includes(r.type));
      if (hasNewFormat) {
        return list.map(row => {
          const normalized = {
            ...makeRow(row.type, row.parentCycleCode, row.code, row.name, row.parentSubblockCode),
            ...row
          };
          if (!Array.isArray(normalized.semesters)) {
            normalized.semesters = emptySemesters();
          }
          normalized.semesters = Array.from({ length: 8 }, (_, i) => ({
            lecture:  normalized.semesters[i]?.lecture  || "",
            practice: normalized.semesters[i]?.practice || "",
            lab:      normalized.semesters[i]?.lab      || "",
            credit:   normalized.semesters[i]?.credit   || ""
          }));
          return normalized;
        });
      }

      const newRows = [];
      let currentCycleCode = "";
      let currentSubblockCode = "";
      let cycleCount = 0;

      list.forEach(oldRow => {
        if (oldRow.type === "block") {
          cycleCount++;
          currentCycleCode = oldRow.blockCode || `Б.${cycleCount}`;
          newRows.push(makeRow("cycle", "", currentCycleCode, oldRow.name));
          
          currentSubblockCode = `${currentCycleCode}.0`;
          newRows.push(makeRow("subblock", currentCycleCode, currentSubblockCode, "Негизги бөлүк"));
        } else if (oldRow.type === "subblock") {
          const sbNo = oldRow.subblockNo || 1;
          currentSubblockCode = `${currentCycleCode}.${sbNo}.0`;
          newRows.push(makeRow("subblock", currentCycleCode, currentSubblockCode, oldRow.name));
        } else if (oldRow.type === "discipline") {
          const isSub = oldRow.subblockNo !== null && oldRow.subblockNo !== undefined;
          const parentSb = isSub ? `${currentCycleCode}.${oldRow.subblockNo}.0` : `${currentCycleCode}.0`;
          
          const newDisc = {
            ...makeRow("discipline", currentCycleCode, oldRow.code || "", oldRow.name, parentSb),
            ...oldRow,
            type: "discipline",
            parentCycleCode: currentCycleCode,
            parentSubblockCode: parentSb
          };
          newRows.push(newDisc);
        } else if (oldRow.type === "grand") {
          newRows.push(makeRow("grand", "", "", oldRow.name || text.grandTotal));
        }
      });

      return newRows;
    }

    function buildHeaderInto(thead) {
      thead.innerHTML = "";

      const tr1 = document.createElement("tr");
      tr1.innerHTML += `<th class="code-col" rowspan="2">${text.cipher || "Шифр"}</th>`;
      tr1.innerHTML += `<th class="name-col" rowspan="2">${text.disciplines || "Дисциплиналар"}</th>`;
      tr1.innerHTML += `<th colspan="2">${text.totalGeneral || "Жалпысы"}</th>`;
      tr1.innerHTML += `<th colspan="5">${text.ofWhich || "Анын ичинен"}</th>`;
      tr1.innerHTML += `<th class="teacher-col" rowspan="2">${text.teacher || "Преподаватель"}</th>`;
      SEMESTERS.forEach((sem, i) => {
        tr1.innerHTML += `<th class="sem-${i + 1} sem-start" colspan="4">${sem}</th>`;
      });
      tr1.innerHTML += `<th colspan="3" class="reporting-header">${text.reportingTitle || "Отчеттуулук"}</th>`;

      const tr2 = document.createElement("tr");
      tr2.innerHTML += `<th>${text.creditsShort}</th>`;
      tr2.innerHTML += `<th>${text.hours}</th>`;
      tr2.innerHTML += `<th>${text.auditoriumShort || "Аудитордук"}</th>`;
      tr2.innerHTML += `<th>${text.lectureShort}</th>`;
      tr2.innerHTML += `<th>${text.practiceShort}</th>`;
      tr2.innerHTML += `<th>${text.labShort}</th>`;
      tr2.innerHTML += `<th>${text.selfStudyShort || "Өз алдынча"}</th>`;
      
      for (let i = 0; i < 8; i++) {
        const s = i + 1;
        tr2.innerHTML += `<th class="sem-${s} sem-start">${text.lectureShort}</th>`;
        tr2.innerHTML += `<th class="sem-${s}">${text.practiceShort}</th>`;
        tr2.innerHTML += `<th class="sem-${s}">${text.labShort}</th>`;
        tr2.innerHTML += `<th class="sem-${s}">${text.creditsShort}</th>`;
      }
      tr2.innerHTML += `<th class="reporting-col">${text.zachetShort || "Зачет"}</th>`;
      tr2.innerHTML += `<th class="reporting-col">${text.examShort || "Сынак"}</th>`;
      tr2.innerHTML += `<th class="reporting-col">${text.courseWorkShort || "Курс. иш"}</th>`;

      thead.appendChild(tr1);
      thead.appendChild(tr2);
    }

    function numberValue(value) {
      const n = parseFloat(String(value || "").replace(",", "."));
      return Number.isNaN(n) ? 0 : n;
    }

    function formatTotal(value) {
      const n = numberValue(value);
      return n === 0 ? "" : String(Number(n.toFixed(2)));
    }

    function getRowStats(row) {
      const credits = row.credits || "";
      const hours = row.hours || "";
      const lecture = numberValue(row.lecture);
      const practice = numberValue(row.practice);
      const lab = numberValue(row.lab);
      
      const auditorium = lecture + practice + lab;
      const selfStudy = Math.max(0, numberValue(row.hours) - auditorium);

      return {
        credits,
        hours,
        auditorium: auditorium > 0 ? String(auditorium) : "",
        lecture: row.lecture || "",
        practice: row.practice || "",
        lab: row.lab || "",
        selfStudy: selfStudy > 0 ? String(selfStudy) : ""
      };
    }

    function calculateTotals() {
      const calcSubblockTotals = {};
      const calcCycleTotals = {};
      const calcGrandTotal = { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };

      // Calculate which cycles and subblocks have children disciplines
      cyclesWithChildren.clear();
      subblocksWithChildren.clear();

      const disciplinesBySubblock = {};
      rows.forEach(row => {
        if (row.type !== "discipline") return;
        if (!disciplinesBySubblock[row.parentSubblockCode]) {
          disciplinesBySubblock[row.parentSubblockCode] = [];
        }
        disciplinesBySubblock[row.parentSubblockCode].push(row);

        if (row.parentCycleCode) {
          cyclesWithChildren.add(row.parentCycleCode);
        }
        if (row.parentSubblockCode) {
          subblocksWithChildren.add(row.parentSubblockCode);
        }
      });

      rows.forEach(row => {
        if (row.type === "discipline") {
          const cyCode = row.parentCycleCode;
          const sbCode = row.parentSubblockCode;

          let cred = 0;
          if (isElectiveSubblock(sbCode)) {
            const siblings = disciplinesBySubblock[sbCode] || [];
            const idx = siblings.indexOf(row);
            if (idx !== -1 && idx % 2 === 0) {
              cred = numberValue(row.credits);
            }
          } else {
            cred = numberValue(row.credits);
          }

          const hour = numberValue(row.hours);
          const lect = numberValue(row.lecture);
          const prac = numberValue(row.practice);
          const lab  = numberValue(row.lab);

          if (sbCode) {
            const key = `${cyCode}_${sbCode}`;
            if (!calcSubblockTotals[key]) calcSubblockTotals[key] = { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };
            calcSubblockTotals[key].credits  += cred;
            calcSubblockTotals[key].hours    += hour;
            calcSubblockTotals[key].lecture  += lect;
            calcSubblockTotals[key].practice += prac;
            calcSubblockTotals[key].lab      += lab;
          }

          if (cyCode) {
            if (!calcCycleTotals[cyCode]) calcCycleTotals[cyCode] = { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };
            calcCycleTotals[cyCode].credits  += cred;
            calcCycleTotals[cyCode].hours    += hour;
            calcCycleTotals[cyCode].lecture  += lect;
            calcCycleTotals[cyCode].practice += prac;
            calcCycleTotals[cyCode].lab      += lab;
          }

          calcGrandTotal.credits  += cred;
          calcGrandTotal.hours    += hour;
          calcGrandTotal.lecture  += lect;
          calcGrandTotal.practice += prac;
          calcGrandTotal.lab      += lab;
        } else if (row.type === "cycle" && !cyclesWithChildren.has(row.code)) {
          // Add cycle itself to grand totals if it has no children disciplines
          calcGrandTotal.credits  += numberValue(row.credits);
          calcGrandTotal.hours    += numberValue(row.hours);
          calcGrandTotal.lecture  += numberValue(row.lecture);
          calcGrandTotal.practice += numberValue(row.practice);
          calcGrandTotal.lab      += numberValue(row.lab);
        }
      });

      rows.forEach(row => {
        row.hasMismatch = false;
        row.mismatchFields = {};

        if (row.type === "subblock") {
          if (subblocksWithChildren.has(row.code)) {
            const key = `${row.parentCycleCode}_${row.code}`;
            const calc = calcSubblockTotals[key] || { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };

            row.credits  = formatTotal(calc.credits);
            row.hours    = formatTotal(calc.hours);
            row.lecture  = formatTotal(calc.lecture);
            row.practice = formatTotal(calc.practice);
            row.lab      = formatTotal(calc.lab);
          }
          row.mismatchFields = {};
          row.hasMismatch = false;
        }

        if (row.type === "cycle") {
          if (cyclesWithChildren.has(row.code)) {
            const calc = calcCycleTotals[row.code] || { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };

            row.credits  = formatTotal(calc.credits);
            row.hours    = formatTotal(calc.hours);
            row.lecture  = formatTotal(calc.lecture);
            row.practice = formatTotal(calc.practice);
            row.lab      = formatTotal(calc.lab);
          }
          row.mismatchFields = {};
          row.hasMismatch = false;
        }

        if (row.type === "grand") {
          row.credits  = formatTotal(calcGrandTotal.credits);
          row.hours    = formatTotal(calcGrandTotal.hours);
          row.lecture  = formatTotal(calcGrandTotal.lecture);
          row.practice = formatTotal(calcGrandTotal.practice);
          row.lab      = formatTotal(calcGrandTotal.lab);

          row.semesters = emptySemesters();
          rows.forEach(d => {
            if (d.type === "discipline" || (d.type === "cycle" && !cyclesWithChildren.has(d.code))) {
              for (let i = 0; i < 8; i++) {
                row.semesters[i].lecture  = formatTotal(numberValue(row.semesters[i].lecture)  + numberValue(d.semesters[i]?.lecture));
                row.semesters[i].practice = formatTotal(numberValue(row.semesters[i].practice) + numberValue(d.semesters[i]?.practice));
                row.semesters[i].lab      = formatTotal(numberValue(row.semesters[i].lab)      + numberValue(d.semesters[i]?.lab));
                row.semesters[i].credit   = formatTotal(numberValue(row.semesters[i].credit)   + numberValue(d.semesters[i]?.credit));
              }
            }
          });
        }
      });
    }

    function inputCell(value, callback, className = "", disabled = false, tdClass = "") {
      const td = document.createElement("td");
      if (tdClass) td.className = tdClass;

      const isEditable = !disabled && isEditMode && !isReadOnly;

      if (isEditable) {
        const input = document.createElement("input");
        input.className = `cell editable-input ${className}`.trim();
        input.value     = value || "";
        input.addEventListener("change", (e) => {
          callback(e.target.value);
          calculateTotals();
          renderBlocks();
        });
        td.appendChild(input);
      } else {
        td.textContent = value || "";
        if (className) td.className = `${td.className} ${className}`.trim();
      }
      return td;
    }

    function getThemeClass(cycleCode) {
      if (!cycleCode) return "";
      let code = String(cycleCode).trim().toLowerCase();
      code = code.replace(/б/g, 'b');
      code = code.replace(/[^a-z0-9]/g, '');
      return code ? `theme-${code}` : "";
    }

    function renderDataRow(row) {
      const tr = document.createElement("tr");

      if (row.id === justAddedRowId) {
        tr.classList.add("just-added-row");
        setTimeout(() => {
          tr.classList.remove("just-added-row");
        }, 3000);
      }

      if (row.type === "discipline") tr.classList.add("discipline-row");
      if (row.type === "cycle")      { 
        tr.classList.add("cycle-row"); 
        if (row.hasMismatch) tr.classList.add("mismatch-row"); 
        const tc = getThemeClass(row.code);
        if (tc) tr.classList.add(tc);
      }
      if (row.type === "subblock")   { 
        tr.classList.add("subblock-row"); 
        if (row.hasMismatch) tr.classList.add("mismatch-row"); 
        const tc = getThemeClass(row.parentCycleCode);
        if (tc) tr.classList.add(tc);
      }
      if (row.type === "grand")      tr.classList.add("grand-total");

      const stats = getRowStats(row);
      const isCycleAuto = row.type === "cycle" && cyclesWithChildren.has(row.code);
      const isAutoCol = row.type === "grand" || row.type === "subblock" || isCycleAuto;

      if (row.type === "cycle") {
        const tdCode = document.createElement("td");
        tdCode.className = "code-col";
        if (!isReadOnly && isEditMode) {
          const cInp = document.createElement("input");
          cInp.className = "cell cycle-code-cell editable-input";
          cInp.style.fontWeight = "800";
          cInp.value = row.code || "";
          cInp.placeholder = "Б.1";
          cInp.addEventListener("change", e => {
            const old = row.code;
            row.code = e.target.value;
            updateChildrenParentCodes(old, "cycle", e.target.value);
            renderBlocks();
          });
          tdCode.appendChild(cInp);
        } else {
          tdCode.textContent = row.code || "";
          tdCode.style.fontWeight = "800";
        }
        tr.appendChild(tdCode);

        const tdName = document.createElement("td");
        tdName.className = "name-col";
        
        const wrap = document.createElement("div");
        wrap.className = "discipline-cell-wrap cycle-name-wrap";

        if (!isReadOnly && isEditMode) {
          const nInp = document.createElement("input");
          nInp.className = "cell cycle-name-cell editable-input";
          nInp.style.flex = "1";
          nInp.style.fontWeight = "800";
          nInp.value = getTranslatedName(row.name || "", language);
          nInp.placeholder = text.blockPlaceholder;
          nInp.addEventListener("change", e => { row.name = e.target.value; });
          wrap.appendChild(nInp);

          const delBtn = document.createElement("button");
          delBtn.type = "button";
          delBtn.className = "mini-btn mini-del edit-only";
          delBtn.innerHTML = "×";
          delBtn.title = text.deleteBlockTitle;
          delBtn.onclick = () => deleteCycle(row.code);
          wrap.appendChild(delBtn);
        } else {
          const span = document.createElement("span");
          span.style.fontWeight = "800";
          span.textContent = getTranslatedName(row.name || "", language);
          wrap.appendChild(span);
        }

        tdName.appendChild(wrap);
        tr.appendChild(tdName);

        const creditsChangeHandler = v => {
          row.credits = v;
          if (!isCycleAuto) {
            const num = parseFloat(String(v || "").replace(",", "."));
            if (!Number.isNaN(num) && num > 0) {
              row.hours = String(num * 30);
            } else {
              row.hours = "";
            }
          }
          calculateTotals();
          renderBlocks();
        };

        const hoursChangeHandler = v => {
          row.hours = v;
          if (!isCycleAuto) {
            const num = parseFloat(String(v || "").replace(",", "."));
            if (!Number.isNaN(num) && num > 0) {
              row.credits = String(Number((num / 30).toFixed(2)));
            } else {
              row.credits = "";
            }
          }
          calculateTotals();
          renderBlocks();
        };

        tr.appendChild(inputCell(row.credits,  creditsChangeHandler, "", isCycleAuto, row.mismatchFields?.credits  ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.hours,    hoursChangeHandler,   "", isCycleAuto, row.mismatchFields?.hours    ? "mismatch-col" : ""));
        tr.appendChild(inputCell(stats.auditorium, v => {}, "", true, row.mismatchFields?.lecture || row.mismatchFields?.practice || row.mismatchFields?.lab ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.lecture,  v => (row.lecture  = v), "", isCycleAuto, row.mismatchFields?.lecture  ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.practice, v => (row.practice = v), "", isCycleAuto, row.mismatchFields?.practice ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.lab,      v => (row.lab      = v), "", isCycleAuto, row.mismatchFields?.lab      ? "mismatch-col" : ""));
        tr.appendChild(inputCell(stats.selfStudy,  v => {},             "", true, ""));

        const tdTeacher = document.createElement("td");
        tdTeacher.className = "teacher-col-cell";
        tr.appendChild(tdTeacher);

        if (isCycleAuto) {
          for (let i = 0; i < 32; i++) {
            const td = document.createElement("td");
            const semIndex = Math.floor(i / 4) + 1;
            const isStart = (i % 4 === 0) ? " sem-start" : "";
            td.className = `subblock-empty-cell sem-${semIndex}${isStart}`;
            tr.appendChild(td);
          }
          for (let i = 0; i < 3; i++) {
            const td = document.createElement("td");
            td.className = "reporting-empty-cell";
            tr.appendChild(td);
          }
        } else {
          row.semesters.forEach((sem, si) => {
            const sc = `sem-${si + 1}`;
            tr.appendChild(inputCell(sem.lecture,  v => (sem.lecture  = v), "", false, `${sc} sem-start`));
            tr.appendChild(inputCell(sem.practice, v => (sem.practice = v), "", false, sc));
            tr.appendChild(inputCell(sem.lab,      v => (sem.lab      = v), "", false, sc));
            tr.appendChild(inputCell(sem.credit,   v => (sem.credit   = v), "", false, sc));
          });
          tr.appendChild(inputCell(row.zachet || "",      v => (row.zachet = v), "reporting-cell", false, "reporting-col-cell"));
          tr.appendChild(inputCell(row.exam || "",        v => (row.exam = v), "reporting-cell", false, "reporting-col-cell"));
          tr.appendChild(inputCell(row.courseWork || "",  v => (row.courseWork = v), "reporting-cell", false, "reporting-col-cell"));
        }

      } else if (row.type === "subblock") {
        const tdCode = document.createElement("td");
        tdCode.className = "code-col";
        if (!isReadOnly && isEditMode) {
          const cInp = document.createElement("input");
          cInp.className = "cell editable-input";
          cInp.style.fontWeight = "700";
          cInp.value = row.code || "";
          cInp.addEventListener("change", e => {
            const old = row.code;
            row.code = e.target.value;
            updateChildrenParentCodes(old, "subblock", e.target.value);
            renderBlocks();
          });
          tdCode.appendChild(cInp);
        } else {
          tdCode.textContent = row.code || "";
          tdCode.style.fontWeight = "700";
        }
        tr.appendChild(tdCode);

        const tdName = document.createElement("td");
        tdName.className = "name-col";
        const wrap = document.createElement("div");
        wrap.className = "discipline-cell-wrap subblock-name-wrap";

        if (!isReadOnly && isEditMode) {
          const nInp = document.createElement("input");
          nInp.className = "cell editable-input";
          nInp.style.flex = "1";
          nInp.style.fontWeight = "700";
          nInp.value = getTranslatedName(row.name || "", language);
          nInp.placeholder = text.subblockPlaceholder;
          nInp.addEventListener("change", e => { row.name = e.target.value; });
          wrap.appendChild(nInp);

          const delBtn = document.createElement("button");
          delBtn.type = "button";
          delBtn.className = "mini-btn mini-del edit-only";
          delBtn.innerHTML = "×";
          delBtn.title = text.deleteBlockTitle;
          delBtn.onclick = () => deleteSubblock(row.parentCycleCode, row.code);
          wrap.appendChild(delBtn);
        } else {
          const span = document.createElement("span");
          span.style.fontWeight = "700";
          span.textContent = getTranslatedName(row.name || "", language);
          wrap.appendChild(span);
        }
        tdName.appendChild(wrap);
        tr.appendChild(tdName);

        tr.appendChild(inputCell(row.credits,  v => (row.credits  = v), "", isAutoCol, row.mismatchFields?.credits  ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.hours,    v => (row.hours    = v), "", isAutoCol, row.mismatchFields?.hours    ? "mismatch-col" : ""));
        tr.appendChild(inputCell(stats.auditorium, v => {},             "", true, row.mismatchFields?.lecture || row.mismatchFields?.practice || row.mismatchFields?.lab ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.lecture,  v => (row.lecture  = v), "", isAutoCol, row.mismatchFields?.lecture  ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.practice, v => (row.practice = v), "", isAutoCol, row.mismatchFields?.practice ? "mismatch-col" : ""));
        tr.appendChild(inputCell(row.lab,      v => (row.lab      = v), "", isAutoCol, row.mismatchFields?.lab      ? "mismatch-col" : ""));
        tr.appendChild(inputCell(stats.selfStudy,  v => {},             "", true, ""));

        const tdTeacher = document.createElement("td");
        tdTeacher.className = "teacher-col-cell";
        tr.appendChild(tdTeacher);

        for (let i = 0; i < 32; i++) {
          const td = document.createElement("td");
          const semIndex = Math.floor(i / 4) + 1;
          const isStart = (i % 4 === 0) ? " sem-start" : "";
          td.className = `subblock-empty-cell sem-${semIndex}${isStart}`;
          tr.appendChild(td);
        }
        for (let i = 0; i < 3; i++) {
          const td = document.createElement("td");
          td.className = "reporting-empty-cell";
          tr.appendChild(td);
        }

      } else if (row.type === "discipline") {
        let nInp = null;
        let populateDatalist = () => {};

        const tdCode = document.createElement("td");
        tdCode.className = "code-col";
        if (!isReadOnly && isEditMode) {
          const cInp = document.createElement("input");
          cInp.className = "cell editable-input";
          cInp.value = row.code || "";
          cInp.addEventListener("change", e => { row.code = e.target.value; });
          tdCode.appendChild(cInp);
        } else {
          tdCode.textContent = row.code || "";
        }
        tr.appendChild(tdCode);

        const tdName = document.createElement("td");
        tdName.className = "name-col";
        const wrap = document.createElement("div");
        wrap.className = "discipline-cell-wrap";

        if (!isReadOnly && isEditMode) {
          nInp = document.createElement("input");
          nInp.className = "cell discipline-cell editable-input";
          nInp.value = row.name || "";
          nInp.placeholder = text.disciplinePlaceholder;

          const datalistId = `dl-${row.id}`;
          nInp.setAttribute("list", datalistId);

          const datalist = document.createElement("datalist");
          datalist.id = datalistId;
          wrap.appendChild(datalist);

          populateDatalist = () => {
            datalist.innerHTML = "";
            let suggestions = [];
            if (row.teacherId) {
              const tObj = teachers.find(t => t.id === row.teacherId);
              suggestions = tObj ? tObj.disciplines || [] : [];
            } else {
              suggestions = [...new Set(teachers.flatMap(t => t.disciplines || []))];
            }
            suggestions.forEach(s => {
              const opt = document.createElement("option");
              opt.value = s;
              datalist.appendChild(opt);
            });
          };

          populateDatalist();

          nInp.addEventListener("change", e => {
            const val = e.target.value;
            row.name = val;
            if (val) {
              autofillDisciplineDetails(row, val, tr, nInp);
            }
            calculateTotals();
            renderBlocks();
          });

          wrap.appendChild(nInp);

          const delBtn = document.createElement("button");
          delBtn.type = "button";
          delBtn.className = "mini-btn mini-del edit-only";
          delBtn.innerHTML = "×";
          delBtn.title = text.deleteBlockTitle;
          delBtn.onclick = () => {
            deleteDiscipline(row);
            calculateTotals();
            renderBlocks();
          };
          wrap.appendChild(delBtn);
        } else {
          const span = document.createElement("span");
          span.textContent = row.name || "";
          wrap.appendChild(span);
        }
        tdName.appendChild(wrap);
        tr.appendChild(tdName);

        const isElective = isElectiveSubblock(row.parentSubblockCode);
        let renderCreditsCol = true;
        let creditsRowspan = 1;
        let creditsChangeHandler = v => {
          row.credits = v;
          if (!isElective) {
            const num = parseFloat(String(v || "").replace(",", "."));
            if (!Number.isNaN(num) && num > 0) {
              row.hours = String(num * 30);
            } else {
              row.hours = "";
            }
          }
          calculateTotals();
          renderBlocks();
        };

        if (isElective) {
          const siblings = rows.filter(r => r.type === "discipline" && r.parentSubblockCode === row.parentSubblockCode);
          const idx = siblings.indexOf(row);
          if (idx !== -1) {
            if (idx % 2 === 0) {
              creditsRowspan = (idx + 1 < siblings.length) ? 2 : 1;
              creditsChangeHandler = v => {
                row.credits = v;
                if (idx + 1 < siblings.length) {
                  siblings[idx + 1].credits = v;
                }
                calculateTotals();
                renderBlocks();
              };
            } else {
              renderCreditsCol = false;
            }
          }
        }

        if (renderCreditsCol) {
          const creditsTd = inputCell(row.credits, creditsChangeHandler);
          if (creditsRowspan > 1) {
            creditsTd.rowSpan = creditsRowspan;
          }
          tr.appendChild(creditsTd);
        }
        tr.appendChild(inputCell(row.hours,    v => {
          row.hours = v;
          if (!isElective) {
            const num = parseFloat(String(v || "").replace(",", "."));
            if (!Number.isNaN(num) && num > 0) {
              row.credits = String(Number((num / 30).toFixed(2)));
            } else {
              row.credits = "";
            }
          }
        }));
        tr.appendChild(inputCell(stats.auditorium, v => {}, "", true));
        tr.appendChild(inputCell(row.lecture,  v => (row.lecture  = v)));
        tr.appendChild(inputCell(row.practice, v => (row.practice = v)));
        tr.appendChild(inputCell(row.lab,      v => (row.lab      = v)));
        tr.appendChild(inputCell(stats.selfStudy,  v => {}, "", true));

        const tdTeacher = document.createElement("td");
        tdTeacher.className = "teacher-col-cell";

        if (isReadOnly || !isEditMode) {
          tdTeacher.textContent = row.teacherName || "-";
        } else {
          const select = document.createElement("select");
          select.className = "cell teacher-select";
          select.style.fontWeight = "600";
          select.style.color = "#0f172a";
          
          const optDefault = document.createElement("option");
          optDefault.value = "";
          optDefault.textContent = text.chooseTeacher;
          select.appendChild(optDefault);

          teachers.forEach(t => {
            const opt = document.createElement("option");
            opt.value = t.id;
            opt.textContent = t.fullName;
            select.appendChild(opt);
          });

          select.value = row.teacherId || "";

          select.addEventListener("change", e => {
            const selectedId = e.target.value;
            row.teacherId = selectedId;
            const tObj = teachers.find(t => t.id === selectedId);
            row.teacherName = tObj ? tObj.fullName : "";
            
            if (tObj && tObj.disciplines && tObj.disciplines.length === 1) {
              row.name = tObj.disciplines[0];
              nInp.value = row.name;
              autofillDisciplineDetails(row, row.name, tr, nInp);
            }

            populateDatalist();
            calculateTotals();
            renderBlocks();
          });

          tdTeacher.appendChild(select);
        }
        tr.appendChild(tdTeacher);

        row.semesters.forEach((sem, si) => {
          const sc = `sem-${si + 1}`;
          tr.appendChild(inputCell(sem.lecture,  v => (sem.lecture  = v), "", false, `${sc} sem-start`));
          tr.appendChild(inputCell(sem.practice, v => (sem.practice = v), "", false, sc));
          tr.appendChild(inputCell(sem.lab,      v => (sem.lab      = v), "", false, sc));
          tr.appendChild(inputCell(sem.credit,   v => (sem.credit   = v), "", false, sc));
        });
        tr.appendChild(inputCell(row.zachet || "",      v => (row.zachet = v), "reporting-cell", false, "reporting-col-cell"));
        tr.appendChild(inputCell(row.exam || "",        v => (row.exam = v), "reporting-cell", false, "reporting-col-cell"));
        tr.appendChild(inputCell(row.courseWork || "",  v => (row.courseWork = v), "reporting-cell", false, "reporting-col-cell"));

      } else if (row.type === "grand") {
        const tdCode = document.createElement("td");
        tdCode.className = "code-col";
        tdCode.style.fontWeight = "800";
        tdCode.textContent = "";
        tr.appendChild(tdCode);

        const tdName = document.createElement("td");
        tdName.className = "name-col";
        tdName.style.fontWeight = "800";
        
        const wrap = document.createElement("div");
        wrap.className = "discipline-cell-wrap";
        
        const span = document.createElement("span");
        span.textContent = getTranslatedName(row.name || text.grandTotal, language);
        wrap.appendChild(span);
        tdName.appendChild(wrap);
        tr.appendChild(tdName);

        tr.appendChild(inputCell(row.credits,  v => {}, "", true));
        tr.appendChild(inputCell(row.hours,    v => {}, "", true));
        tr.appendChild(inputCell(stats.auditorium, v => {}, "", true));
        tr.appendChild(inputCell(row.lecture,  v => {}, "", true));
        tr.appendChild(inputCell(row.practice, v => {}, "", true));
        tr.appendChild(inputCell(row.lab,      v => {}, "", true));
        tr.appendChild(inputCell(stats.selfStudy,  v => {}, "", true));

        const tdTeacher = document.createElement("td");
        tdTeacher.className = "teacher-col-cell";
        tr.appendChild(tdTeacher);

        row.semesters.forEach((sem, si) => {
          const sc = `sem-${si + 1}`;
          tr.appendChild(inputCell(sem.lecture,  v => {}, "", true, `${sc} sem-start`));
          tr.appendChild(inputCell(sem.practice, v => {}, "", true, sc));
          tr.appendChild(inputCell(sem.lab,      v => {}, "", true, sc));
          tr.appendChild(inputCell(sem.credit,   v => {}, "", true, sc));
        });
        tr.appendChild(inputCell(row.zachet || "",      v => {}, "", true, "reporting-col-cell"));
        tr.appendChild(inputCell(row.exam || "",        v => {}, "", true, "reporting-col-cell"));
        tr.appendChild(inputCell(row.courseWork || "",  v => {}, "", true, "reporting-col-cell"));
      }

      return tr;
    }

    function getFilteredRowsForRender() {
      if (!isTeacher) return rows;

      const activeTeacherId = teacherId || teacher?.id || teacher?.uid || "";
      if (!activeTeacherId) return rows;

      const keptDisciplines = rows.filter(r => r.type === "discipline" && String(r.teacherId) === String(activeTeacherId));
      
      const filtered = [];
      rows.forEach(r => {
        if (r.type === "discipline") {
          if (keptDisciplines.some(kd => kd.id === r.id)) {
            filtered.push(r);
          }
        } else if (r.type === "subblock") {
          const subblockDiscs = keptDisciplines.filter(kd => kd.parentCycleCode === r.parentCycleCode && kd.parentSubblockCode === r.code);
          if (subblockDiscs.length > 0) {
            const tempSb = JSON.parse(JSON.stringify(r));
            const stats = { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };
            subblockDiscs.forEach(kd => {
              stats.credits += numberValue(kd.credits);
              stats.hours += numberValue(kd.hours);
              stats.lecture += numberValue(kd.lecture);
              stats.practice += numberValue(kd.practice);
              stats.lab += numberValue(kd.lab);
            });
            tempSb.credits = formatTotal(stats.credits);
            tempSb.hours = formatTotal(stats.hours);
            tempSb.lecture = formatTotal(stats.lecture);
            tempSb.practice = formatTotal(stats.practice);
            tempSb.lab = formatTotal(stats.lab);
            filtered.push(tempSb);
          }
        } else if (r.type === "cycle") {
          const cycleDiscs = keptDisciplines.filter(kd => kd.parentCycleCode === r.code);
          if (cycleDiscs.length > 0) {
            const tempCy = JSON.parse(JSON.stringify(r));
            const stats = { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };
            cycleDiscs.forEach(kd => {
              stats.credits += numberValue(kd.credits);
              stats.hours += numberValue(kd.hours);
              stats.lecture += numberValue(kd.lecture);
              stats.practice += numberValue(kd.practice);
              stats.lab += numberValue(kd.lab);
            });
            tempCy.credits = formatTotal(stats.credits);
            tempCy.hours = formatTotal(stats.hours);
            tempCy.lecture = formatTotal(stats.lecture);
            tempCy.practice = formatTotal(stats.practice);
            tempCy.lab = formatTotal(stats.lab);
            filtered.push(tempCy);
          }
        } else if (r.type === "grand") {
          const tempGrand = JSON.parse(JSON.stringify(r));
          const stats = { credits: 0, hours: 0, lecture: 0, practice: 0, lab: 0 };
          keptDisciplines.forEach(kd => {
            stats.credits += numberValue(kd.credits);
            stats.hours += numberValue(kd.hours);
            stats.lecture += numberValue(kd.lecture);
            stats.practice += numberValue(kd.practice);
            stats.lab += numberValue(kd.lab);
          });
          tempGrand.credits = formatTotal(stats.credits);
          tempGrand.hours = formatTotal(stats.hours);
          tempGrand.lecture = formatTotal(stats.lecture);
          tempGrand.practice = formatTotal(stats.practice);
          tempGrand.lab = formatTotal(stats.lab);

          tempGrand.semesters = Array.from({ length: 8 }, () => ({
            lecture: "", practice: "", lab: "", credit: ""
          }));
          keptDisciplines.forEach(kd => {
            for (let i = 0; i < 8; i++) {
              tempGrand.semesters[i].lecture  = formatTotal(numberValue(tempGrand.semesters[i].lecture)  + numberValue(kd.semesters[i]?.lecture));
              tempGrand.semesters[i].practice = formatTotal(numberValue(tempGrand.semesters[i].practice) + numberValue(kd.semesters[i]?.practice));
              tempGrand.semesters[i].lab      = formatTotal(numberValue(tempGrand.semesters[i].lab)      + numberValue(kd.semesters[i]?.lab));
              tempGrand.semesters[i].credit   = formatTotal(numberValue(tempGrand.semesters[i].credit)   + numberValue(kd.semesters[i]?.credit));
            }
          });
          filtered.push(tempGrand);
        }
      });

      return filtered;
    }

    function renderBlocks() {
      const container = $("blocksContainer");
      const tabsBar   = $("filterTabsBar");
      if (!container) return;

      // 1. Сохраняем скролл окна
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      // 2. Сохраняем скролл обертки таблицы
      let tableWrapScrollLeft = 0;
      let tableWrapScrollTop = 0;
      const oldTableWrap = container.querySelector(".block-table-wrap");
      if (oldTableWrap) {
        tableWrapScrollLeft = oldTableWrap.scrollLeft;
        tableWrapScrollTop = oldTableWrap.scrollTop;
      }

      // 3. Сохраняем фокус и положение курсора (каретки)
      let activeCellInfo = null;
      if (document.activeElement && (document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "SELECT") && container.contains(document.activeElement)) {
        const activeInput = document.activeElement;
        const activeTd = activeInput.closest("td");
        const activeTr = activeInput.closest("tr");
        if (activeTr && activeTd) {
          const rowIndex = Array.from(activeTr.parentNode.children).indexOf(activeTr);
          const colIndex = Array.from(activeTr.children).indexOf(activeTd);
          let caretStart = 0;
          let caretEnd = 0;
          try {
            caretStart = activeInput.selectionStart;
            caretEnd = activeInput.selectionEnd;
          } catch (e) {
            // Игнорируем для типов ввода, которые не поддерживают это
          }
          activeCellInfo = { rowIndex, colIndex, caretStart, caretEnd };
        }
      }

      container.innerHTML = "";
      calculateTotals();

      const activeRows = getFilteredRowsForRender();

      const cycleCodes = [...new Set(
        activeRows.filter(r => r.type === "cycle" && r.code).map(r => r.code)
      )];

      if (tabsBar) {
        tabsBar.innerHTML = "";

        const makeTab = (label, value) => {
          const btn = document.createElement("button");
          btn.type      = "button";
          btn.className = `filter-tab${currentFilter === value ? " active" : ""}`;
          btn.textContent = label;
          btn.onclick   = () => { currentFilter = value; renderBlocks(); };
          return btn;
        };

        tabsBar.appendChild(makeTab(text.filterAll || "Все циклы", "all"));

        cycleCodes.forEach(code => {
          const cRow = activeRows.find(r => r.type === "cycle" && r.code === code);
          tabsBar.appendChild(makeTab(cRow?.code || code, code));
        });
      }

      const tableWrap = document.createElement("div");
      tableWrap.className = "block-table-wrap";

      const tableEl = document.createElement("table");
      tableEl.className = "plan-block-table";

      const thead = document.createElement("thead");
      buildHeaderInto(thead);
      tableEl.appendChild(thead);

      const tbody = document.createElement("tbody");

      cycleCodes.forEach(cycleCode => {
        if (currentFilter !== "all" && currentFilter !== cycleCode) return;

        const cycleRow  = activeRows.find(r => r.type === "cycle" && r.code === cycleCode);
        const subblocks = activeRows.filter(r => r.type === "subblock" && r.parentCycleCode === cycleCode);

        if (cycleRow) {
          tbody.appendChild(renderDataRow(cycleRow));
        }

        // Render disciplines that belong directly to the cycle (no subblock or empty parentSubblockCode)
        const directDisciplines = activeRows.filter(r =>
          r.type === "discipline" &&
          r.parentCycleCode === cycleCode &&
          (!r.parentSubblockCode || r.parentSubblockCode === "")
        );
        directDisciplines.forEach(d => tbody.appendChild(renderDataRow(d)));

        // Add subject placeholder row under direct subjects in edit mode (if there are direct disciplines OR if there are no subblocks at all)
        if (isEditMode && !isReadOnly && (directDisciplines.length > 0 || subblocks.length === 0)) {
          const addDiscTr = document.createElement("tr");
          addDiscTr.className = "add-row-placeholder edit-only";

          const td1 = document.createElement("td");
          td1.className = "code-col";

          const td2 = document.createElement("td");
          td2.className = "name-col";

          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "add-inline-btn";
          btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-plus" style="margin-right: 6px;"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>${text.addDisciplineTitle || "Добавить предмет"}`;
          btn.onclick = () => addDisciplineToSubblock(cycleCode, "");
          td2.appendChild(btn);

          addDiscTr.appendChild(td1);
          addDiscTr.appendChild(td2);

          const td3 = document.createElement("td");
          td3.colSpan = 40;
          addDiscTr.appendChild(td3);

          const tc = getThemeClass(cycleCode);
          if (tc) addDiscTr.classList.add(tc);

          tbody.appendChild(addDiscTr);
        }

        subblocks.forEach(sb => {
          // Skip dummy subblock (with empty code and name) since its disciplines are already rendered as directDisciplines
          if ((!sb.code || sb.code.trim() === "") && (!sb.name || sb.name.trim() === "")) {
            return;
          }
          tbody.appendChild(renderDataRow(sb));

          const disciplines = activeRows.filter(r =>
            r.type === "discipline" &&
            r.parentCycleCode === cycleCode &&
            r.parentSubblockCode === sb.code
          );
          disciplines.forEach(d => tbody.appendChild(renderDataRow(d)));

          // Add subject placeholder row under subjects list in edit mode
          if (isEditMode && !isReadOnly) {
            const addDiscTr = document.createElement("tr");
            addDiscTr.className = "add-row-placeholder edit-only";

            const td1 = document.createElement("td");
            td1.className = "code-col";

            const td2 = document.createElement("td");
            td2.className = "name-col";

            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "add-inline-btn";
            btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-plus" style="margin-right: 6px;"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>${text.addDisciplineTitle || "Добавить предмет"}`;
            btn.onclick = () => addDisciplineToSubblock(cycleCode, sb.code);
            td2.appendChild(btn);

            addDiscTr.appendChild(td1);
            addDiscTr.appendChild(td2);

            const td3 = document.createElement("td");
            td3.colSpan = 40;
            addDiscTr.appendChild(td3);

            const tc = getThemeClass(cycleCode);
            if (tc) addDiscTr.classList.add(tc);

            tbody.appendChild(addDiscTr);
          }
        });

        // Add subsection placeholder row under subsections list in edit mode
        if (isEditMode && !isReadOnly) {
          const addSbTr = document.createElement("tr");
          addSbTr.className = "add-row-placeholder edit-only";

          const td1 = document.createElement("td");
          td1.className = "code-col";

          const td2 = document.createElement("td");
          td2.className = "name-col";

          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "add-inline-btn subblock-btn";
          btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-plus" style="margin-right: 6px;"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>${(text.addSubblock || "+ Подраздел").replace(/^\+\s*/, '')}`;
          btn.onclick = () => addSubblockToCycle(cycleCode);
          td2.appendChild(btn);

          addSbTr.appendChild(td1);
          addSbTr.appendChild(td2);

          const td3 = document.createElement("td");
          td3.colSpan = 40;
          addSbTr.appendChild(td3);

          const tc = getThemeClass(cycleCode);
          if (tc) addSbTr.classList.add(tc);

          tbody.appendChild(addSbTr);
        }
      });

      if (currentFilter === "all") {
        const grandRow = activeRows.find(r => r.type === "grand");
        if (grandRow) {
          tbody.appendChild(renderDataRow(grandRow));

          if (!isTeacher && !isLab) {
            // Helper function to check if semester is present in zachet/exam/coursework string
            const hasSemesterInReporting = (fieldValue, semNum) => {
              if (!fieldValue) return false;
              const parts = String(fieldValue).split(/[\s,;\/\\]+/);
              return parts.some(p => {
                const val = parseInt(p.trim(), 10);
                return !Number.isNaN(val) && val === semNum;
              });
            };

            // Initialize variables for calculations
            const semWeeklyHours = Array(8).fill(0);
            const semCredits = Array(8).fill(0);
            const semZachets = Array(8).fill(0);
            const semExams = Array(8).fill(0);

            rows.forEach(row => {
              // Count disciplines and childless cycles
              if (row.type === "discipline" || (row.type === "cycle" && !cyclesWithChildren.has(row.code))) {
                for (let s = 1; s <= 8; s++) {
                  const sem = row.semesters[s - 1];
                  if (sem) {
                    semWeeklyHours[s - 1] += numberValue(sem.lecture) + numberValue(sem.practice) + numberValue(sem.lab);
                    semCredits[s - 1] += numberValue(sem.credit);
                  }
                  if (hasSemesterInReporting(row.zachet, s)) {
                    semZachets[s - 1]++;
                  }
                  if (hasSemesterInReporting(row.exam, s)) {
                    semExams[s - 1]++;
                  }
                }
              }
            });

            const totalZachets = semZachets.reduce((a, b) => a + b, 0);
            const totalExams = semExams.reduce((a, b) => a + b, 0);
            const totalCredits = semCredits.reduce((a, b) => a + b, 0);

            const formatSummaryVal = val => val === 0 ? "" : String(val).replace(".", ",");

            // Row 1: Жумасы сааттын саны / Hours per week
            const tr1 = document.createElement("tr");
            tr1.className = "summary-row";

            const tdLabel1 = document.createElement("td");
            tdLabel1.colSpan = 10;
            tdLabel1.className = "label-col";
            tdLabel1.textContent = text.weeklyHours || "Жумасы сааттын саны";
            tr1.appendChild(tdLabel1);

            for (let s = 1; s <= 8; s++) {
              const tdSem = document.createElement("td");
              tdSem.colSpan = 4;
              tdSem.className = `sem-${s} sem-start`;
              tdSem.textContent = formatSummaryVal(semWeeklyHours[s - 1]);
              tr1.appendChild(tdSem);
            }

            const tdEnd1 = document.createElement("td");
            tdEnd1.colSpan = 3;
            tdEnd1.className = "reporting-col-cell";
            tr1.appendChild(tdEnd1);

            tbody.appendChild(tr1);

            // Row 2:  Кредиттердин саны / Credits per semester
            const tr2 = document.createElement("tr");
            tr2.className = "summary-row";

            const tdLabel2 = document.createElement("td");
            tdLabel2.colSpan = 10;
            tdLabel2.className = "label-col";
            tdLabel2.textContent = text.semesterCredits || "Семестрдеги кредиттин саны";
            tr2.appendChild(tdLabel2);

            for (let s = 1; s <= 8; s++) {
              const tdSem = document.createElement("td");
              tdSem.colSpan = 4;
              tdSem.className = `sem-${s} sem-start`;
              tdSem.textContent = formatSummaryVal(semCredits[s - 1]);
              tr2.appendChild(tdSem);
            }

            const tdEnd2 = document.createElement("td");
            tdEnd2.colSpan = 3;
            tdEnd2.className = "reporting-col-cell";
            tdEnd2.textContent = formatSummaryVal(totalCredits);
            tr2.appendChild(tdEnd2);

            tbody.appendChild(tr2);

            // Row 3: Зачеттордун саны / Zachets per semester
            const tr3 = document.createElement("tr");
            tr3.className = "summary-row";

            const tdLabel3 = document.createElement("td");
            tdLabel3.colSpan = 10;
            tdLabel3.className = "label-col";
            tdLabel3.textContent = text.semesterZachets || "Семестрдеги зачеттордун саны";
            tr3.appendChild(tdLabel3);

            for (let s = 1; s <= 8; s++) {
              const tdSem = document.createElement("td");
              tdSem.colSpan = 4;
              tdSem.className = `sem-${s} sem-start`;
              tdSem.textContent = formatSummaryVal(semZachets[s - 1]);
              tr3.appendChild(tdSem);
            }

            const tdEnd3 = document.createElement("td");
            tdEnd3.colSpan = 3;
            tdEnd3.className = "reporting-col-cell";
            tdEnd3.textContent = formatSummaryVal(totalZachets);
            tr3.appendChild(tdEnd3);

            tbody.appendChild(tr3);

            // Row 4: Сынактардын саны / Exams per semester
            const tr4 = document.createElement("tr");
            tr4.className = "summary-row";

            const tdLabel4 = document.createElement("td");
            tdLabel4.colSpan = 10;
            tdLabel4.className = "label-col";
            tdLabel4.textContent = text.semesterExams || "Семестрдеги сынактардын саны";
            tr4.appendChild(tdLabel4);

            for (let s = 1; s <= 8; s++) {
              const tdSem = document.createElement("td");
              tdSem.colSpan = 4;
              tdSem.className = `sem-${s} sem-start`;
              tdSem.textContent = formatSummaryVal(semExams[s - 1]);
              tr4.appendChild(tdSem);
            }

            const tdEnd4 = document.createElement("td");
            tdEnd4.colSpan = 3;
            tdEnd4.className = "reporting-col-cell";
            tdEnd4.textContent = formatSummaryVal(totalExams);
            tr4.appendChild(tdEnd4);

            tbody.appendChild(tr4);
          }
        }
      }

      tableEl.appendChild(tbody);
      tableWrap.appendChild(tableEl);
      container.appendChild(tableWrap);

      // Восстанавливаем скролл обертки таблицы
      tableWrap.scrollLeft = tableWrapScrollLeft;
      tableWrap.scrollTop = tableWrapScrollTop;

      // Восстанавливаем скролл окна
      window.scrollTo(scrollX, scrollY);

      // Восстанавливаем фокус и положение каретки
      if (activeCellInfo) {
        const newTbody = tableEl.querySelector("tbody");
        if (newTbody && newTbody.children[activeCellInfo.rowIndex]) {
          const tr = newTbody.children[activeCellInfo.rowIndex];
          if (tr.children[activeCellInfo.colIndex]) {
            const td = tr.children[activeCellInfo.colIndex];
            const input = td.querySelector("input") || td.querySelector("select");
            if (input) {
              input.focus();
              try {
                if (input.setSelectionRange) {
                  input.setSelectionRange(activeCellInfo.caretStart, activeCellInfo.caretEnd);
                }
              } catch (e) {
                // Игнорируем
              }
            }
          }
        }
      }

      updateModeUI();
    }


    function getCurrentPlanInfo() {
      const direction = $("direction")?.value || "ИВТ";
      const year      = $("year")?.value      || "2025-2026";
      const form      = $("studyForm")?.value || "Очная";
      return { direction, year, form, planId: makePlanDocId(direction, year, form) };
    }

    async function loadYears() {
      try {
        const snap = await getDoc(doc(db, "settings", "curriculumYears"));
        if (snap.exists()) {
          const d = snap.data() || {};
          const dbYears = Array.isArray(d.years) ? d.years : Array.isArray(d.items) ? d.items : DEFAULT_YEARS;
          years = dbYears.filter(y => y !== "2024-2025");
          if (dbYears.includes("2024-2025")) {
            await setDoc(doc(db, "settings", "curriculumYears"), {
              ...d,
              years,
              items: years,
              updatedAt: Date.now()
            }, { merge: true });
          }
        } else {
          years = DEFAULT_YEARS;
          await setDoc(doc(db, "settings", "curriculumYears"), {
            id: "curriculumYears", years: DEFAULT_YEARS, items: DEFAULT_YEARS,
            createdAt: Date.now(), updatedAt: Date.now()
          });
        }
        renderYearsSelect();
      } catch (err) {
        console.error(err);
        years = DEFAULT_YEARS;
        renderYearsSelect();
        setStatus(text.loadYearsError);
      }
    }

    function renderYearsSelect() {
      const sel = $("year");
      if (!sel) return;
      const cur = sel.value || localStorage.getItem("academicYear") || "2025-2026";
      sel.innerHTML = "";
      years.forEach(y => {
        const opt = document.createElement("option");
        opt.value = y;
        opt.textContent = y.replace("-", "–");
        sel.appendChild(opt);
      });
      sel.value = years.includes(cur) ? cur : (years[0] || "");
    }

    async function addYear() {
      if (isReadOnly) return;
      const v = window.prompt(text.promptYear);
      if (!v?.trim()) return;
      const cleanYear = v.trim();
      if (years.includes(cleanYear)) { $("year").value = cleanYear; await loadPlan(); return; }
      years = [...years, cleanYear];
      try {
        await setDoc(doc(db, "settings", "curriculumYears"),
          { id: "curriculumYears", years, items: years, updatedAt: Date.now() },
          { merge: true }
        );
        renderYearsSelect();
        $("year").value = cleanYear;
        localStorage.setItem("academicYear", cleanYear);
        rows = defaultRows(); isEditMode = true; renderBlocks();
        setStatus(text.newYearAdded);
      } catch (err) { console.error(err); setStatus(text.addYearError); }
    }

    async function savePlan() {
      if (isReadOnly) return;
      calculateTotals();
      const { direction, year, form, planId } = getCurrentPlanInfo();
      const cleanRows = rows.filter(r => !(r.type === "subblock" && (!r.code || r.code.trim() === "") && (!r.name || r.name.trim() === "")));
      try {
        const payload = {
          id: planId, direction, year, studyForm: form,
          qualification: text.bachelor, rows: cleanRows,
          updatedAt: Date.now(), updatedAtText: new Date().toISOString()
        };
        await setDoc(doc(db, "curriculumPlans", planId), payload, { merge: true });
        planCache[planId] = payload;
        localStorage.setItem("academicYear", year);
        rows = normalizeRows(cleanRows);
        isEditMode = false; renderBlocks(); setStatus(text.saved);
      } catch (err) { console.error(err); setStatus(text.saveError); }
    }

    async function loadPlan() {
      try {
        const { year, planId } = getCurrentPlanInfo();
        localStorage.setItem("academicYear", year);

        if (planCache[planId]) {
          rows = normalizeRows(planCache[planId].rows || defaultRows());
          isEditMode = false;
          renderBlocks();
          setStatus(text.loaded);
          return;
        }

        const snap = await getDoc(doc(db, "curriculumPlans", planId));
        if (!snap.exists()) {
          rows = defaultRows(); isEditMode = !isReadOnly; renderBlocks();
          setStatus(isReadOnly ? text.noDataTeacher : text.noDataAdmin);
          return;
        }
        const payload = snap.data() || {};
        planCache[planId] = payload;
        rows = normalizeRows(payload.rows || defaultRows());
        isEditMode = false; renderBlocks(); setStatus(text.loaded);
      } catch (err) {
        console.error(err);
        rows = defaultRows(); isEditMode = !isReadOnly; renderBlocks(); setStatus(text.loadError);
      }
    }

    function updateChildrenParentCodes(oldCode, type, newCode) {
      rows.forEach(r => {
        if (type === "cycle") {
          if (r.parentCycleCode === oldCode) {
            r.parentCycleCode = newCode;
            if (r.type === "subblock" && r.code.startsWith(oldCode + ".")) {
              const prevSbCode = r.code;
              r.code = newCode + r.code.substr(oldCode.length);
              updateChildrenParentCodes(prevSbCode, "subblock", r.code);
            }
          }
        } else if (type === "subblock") {
          if (r.parentSubblockCode === oldCode) {
            r.parentSubblockCode = newCode;
            if (r.type === "discipline" && r.code.startsWith(oldCode.replace(/\.0$/, ""))) {
              const oldPrefix = oldCode.replace(/\.0$/, "");
              const newPrefix = newCode.replace(/\.0$/, "");
              r.code = newPrefix + r.code.substr(oldPrefix.length);
            }
          }
        }
      });
    }

    function addDisciplineToSubblock(parentCycleCode, parentSubblockCode) {
      if (!isEditMode || isReadOnly) return;
      let lastIdx = -1;
      rows.forEach((r, i) => {
        if (r.parentCycleCode === parentCycleCode && r.parentSubblockCode === parentSubblockCode) {
          lastIdx = i;
        }
      });
      if (lastIdx === -1) {
        lastIdx = rows.findIndex(r => r.type === "subblock" && r.parentCycleCode === parentCycleCode && r.code === parentSubblockCode);
      }
      if (lastIdx === -1) {
        lastIdx = rows.findIndex(r => r.type === "cycle" && r.code === parentCycleCode);
      }
      
      const subDisc = rows.filter(r => r.type === "discipline" && r.parentCycleCode === parentCycleCode && r.parentSubblockCode === parentSubblockCode);
      const nextNo = subDisc.length + 1;
      const basePrefix = parentSubblockCode ? parentSubblockCode.replace(/\.0$/, "") : parentCycleCode;
      const cleanPrefix = basePrefix.endsWith(".") ? basePrefix.slice(0, -1) : basePrefix;
      const discCode = `${cleanPrefix}.${nextNo}`;

      const newDisc = makeRow("discipline", parentCycleCode, discCode, "", parentSubblockCode);
      justAddedRowId = newDisc.id;
      rows.splice(lastIdx + 1, 0, newDisc);
      renderBlocks(); setStatus(text.disciplineAdded);
    }

    function addSubblockToCycle(parentCycleCode) {
      if (!isEditMode || isReadOnly) return;
      let lastIdx = -1;
      rows.forEach((r, i) => {
        if (r.parentCycleCode === parentCycleCode) {
          lastIdx = i;
        }
      });
      if (lastIdx === -1) {
        lastIdx = rows.findIndex(r => r.type === "cycle" && r.code === parentCycleCode);
      }

      const existingSb = rows.filter(r => r.type === "subblock" && r.parentCycleCode === parentCycleCode);
      const nextSbNo = existingSb.length + 1;
      const sbCode = `${parentCycleCode}.${nextSbNo}.0`;
      const discCode = `${parentCycleCode}.${nextSbNo}.1`;

      const newSb = makeRow("subblock", parentCycleCode, sbCode, text.newSubblock);
      const newDisc = makeRow("discipline", parentCycleCode, discCode, "", sbCode);
      justAddedRowId = newSb.id;

      rows.splice(lastIdx + 1, 0, newSb, newDisc);
      renderBlocks(); setStatus(text.blockAdded);
    }

    function addCycle() {
      if (!isEditMode || isReadOnly) return;
      const grandIdx = rows.findIndex(r => r.type === "grand");
      const nextCycleNo = rows.filter(r => r.type === "cycle").length + 1;
      const cycleCode = `Б.${nextCycleNo}`;
      const sbCode = `${cycleCode}.1.0`;
      const discCode = `${cycleCode}.1.1`;

      const newCy = makeRow("cycle", "", cycleCode, text.blockPlaceholder);
      const newSb = makeRow("subblock", cycleCode, sbCode, text.newSubblock);
      const newDisc = makeRow("discipline", cycleCode, discCode, "", sbCode);
      justAddedRowId = newCy.id;

      rows.splice(grandIdx === -1 ? rows.length : grandIdx, 0, newCy, newSb, newDisc);
      renderBlocks(); setStatus(text.blockAdded);
    }

    function deleteCycle(cycleCode) {
      if (!isEditMode || isReadOnly) return;
      if (!window.confirm(text.confirmDeleteBlock)) return;
      rows = rows.filter(r => r.code !== cycleCode && r.parentCycleCode !== cycleCode);
      renderBlocks(); setStatus(text.blockDeleted);
    }

    function deleteSubblock(parentCycleCode, subblockCode) {
      if (!isEditMode || isReadOnly) return;
      if (!window.confirm(text.confirmDeleteSubblock)) return;
      rows = rows.filter(r => !(r.parentCycleCode === parentCycleCode && (r.code === subblockCode || r.parentSubblockCode === subblockCode)));
      renderBlocks(); setStatus(text.blockDeleted);
    }

    function deleteDiscipline(row) {
      const idx = rows.indexOf(row);
      if (idx !== -1) {
        rows.splice(idx, 1);
      }
    }

    function enableEdit() {
      if (isReadOnly) return;
      isEditMode = true; renderBlocks(); setStatus(text.editEnabled);
    }

    function exportExcel() {
      calculateTotals();
      const { direction, year, form } = getCurrentPlanInfo();
      const data = [];

      data.push([text.pageTitle]);
      data.push([text.direction.replace(":", ""), direction]);
      data.push([text.academicYear.replace(":", ""), year]);
      data.push([text.studyForm.replace(":", ""), form]);
      data.push([text.qualification.replace(":", ""), text.bachelor]);
      data.push([]);

      const header = [
        text.cipher || "Шифр", text.disciplines, text.creditsShort, text.hours,
        text.auditoriumShort || "Аудит.", text.lectureShort, text.practiceShort, text.labShort,
        text.selfStudyShort || "Өз алдынча", text.teacher || "Преподаватель"
      ];
      for (let s = 1; s <= 8; s++) {
        header.push(
          `${text[`semester${s}`]} ${text.lectureShort}`,
          `${text[`semester${s}`]} ${text.practiceShort}`,
          `${text[`semester${s}`]} ${text.labShort}`,
          `${text[`semester${s}`]} ${text.creditsShort}`
        );
      }
      header.push(text.zachetShort || "Зачет", text.examShort || "Сынак", text.courseWorkShort || "Курс. иш");
      data.push(header);

      rows.forEach(row => {
        const stats = getRowStats(row);
        if (row.type === "cycle") {
          data.push([
            row.code || "", getTranslatedName(row.name || "", language), stats.credits, stats.hours,
            stats.auditorium, stats.lecture, stats.practice, stats.lab, stats.selfStudy, "",
            ...Array(35).fill("")
          ]);
        } else if (row.type === "subblock") {
          if ((!row.code || row.code.trim() === "") && (!row.name || row.name.trim() === "")) {
            return;
          }
          data.push([
            row.code || "", getTranslatedName(row.name || "", language), stats.credits, stats.hours,
            stats.auditorium, stats.lecture, stats.practice, stats.lab, stats.selfStudy, "",
            ...Array(35).fill("")
          ]);
        } else if (row.type === "discipline") {
          let excelCredits = stats.credits;
          if (isElectiveSubblock(row.parentSubblockCode)) {
            const siblings = rows.filter(r => r.type === "discipline" && r.parentSubblockCode === row.parentSubblockCode);
            const idx = siblings.indexOf(row);
            if (idx !== -1 && idx % 2 !== 0) {
              excelCredits = "";
            }
          }

          const rd = [
            row.code || "", row.name || "", excelCredits, stats.hours,
            stats.auditorium, stats.lecture, stats.practice, stats.lab, stats.selfStudy,
            row.teacherName || ""
          ];
          row.semesters?.forEach(sem => {
            rd.push(sem.lecture || "", sem.practice || "", sem.lab || "", sem.credit || "");
          });
          rd.push(row.zachet || "", row.exam || "", row.courseWork || "");
          data.push(rd);
        } else if (row.type === "grand") {
          data.push([
            "", getTranslatedName(row.name || text.grandTotal, language), stats.credits, stats.hours,
            stats.auditorium, stats.lecture, stats.practice, stats.lab, stats.selfStudy, "",
            ...Array(35).fill("")
          ]);
        }
      });

      const ws = XLSX.utils.aoa_to_sheet(data);
      ws["!cols"] = [
        { wch: 12 }, { wch: 42 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 18 },
        ...Array.from({ length: 35 }, () => ({ wch: 10 }))
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, text.sheetName);
      XLSX.writeFile(wb, `${text.fileName}_${direction}_${year}_${form}.xlsx`);
      setStatus(text.exported);
    }

    function setStatus(msg) {
      const el = $("statusText");
      if (el) el.textContent = msg;
    }

    function updateModeUI() {
      root.classList.toggle("readonly",     !isEditMode || isReadOnly);
      root.classList.toggle("teacher-mode", isTeacher);
      root.classList.toggle("lab-mode",     isLab);

      const badge = $("modeBadge");
      if (!badge) return;
      if (isReadOnly) { badge.textContent = text.viewMode; badge.className = "mode-badge mode-read"; return; }
      badge.textContent = isEditMode ? text.editMode : text.viewMode;
      badge.className   = isEditMode ? "mode-badge mode-edit" : "mode-badge mode-read";
    }

    const directionSelect = $("direction");
    const yearSelect      = $("year");
    const studyFormSelect = $("studyForm");
    const editBtn         = $("editBtn");
    const saveBtn         = $("saveBtn");
    const addBlockBtn     = $("addBlockBtn");
    const addYearBtn      = $("addYearBtn");
    const excelBtn        = $("excelBtn");

    const handleDirectionChange = e => {
      directionRef.current = e.target.value;
      loadPlan();
    };

    const handleYearChange = e => {
      yearRef.current = e.target.value;
      loadPlan();
    };

    const handleStudyFormChange = e => {
      studyFormRef.current = e.target.value;
      loadPlan();
    };

    if (directionSelect) directionSelect.addEventListener("change", handleDirectionChange);
    if (yearSelect)       yearSelect.addEventListener("change", handleYearChange);
    if (studyFormSelect)  studyFormSelect.addEventListener("change", handleStudyFormChange);

    if (!isReadOnly) {
      if (editBtn)     editBtn.addEventListener("click",     enableEdit);
      if (saveBtn)     saveBtn.addEventListener("click",     savePlan);
      if (addBlockBtn) addBlockBtn.addEventListener("click", addCycle);
      if (addYearBtn)  addYearBtn.addEventListener("click",  addYear);
    }
    if (excelBtn) excelBtn.addEventListener("click", exportExcel);

    async function loadAllKnownDisciplines() {
      try {
        const snap = await getDocs(collection(db, "curriculumPlans"));
        const data = {};
        snap.docs.forEach(doc => {
          const payload = doc.data() || {};
          const planRows = payload.rows || [];
          planRows.forEach(r => {
            if (r.type === "discipline" && r.name && r.name.trim()) {
              const nameKey = r.name.trim().toLowerCase();
              const hasData = r.credits || r.hours || r.lecture || r.practice || r.lab;
              if (hasData) {
                data[nameKey] = {
                  code: r.code || "",
                  credits: r.credits || "",
                  hours: r.hours || "",
                  lecture: r.lecture || "",
                  practice: r.practice || "",
                  lab: r.lab || "",
                  semesters: JSON.parse(JSON.stringify(r.semesters || emptySemesters()))
                };
              }
            }
          });
        });
        knownDisciplinesData = data;
      } catch (err) {
        console.error("Error loading known disciplines data:", err);
      }
    }

    function autofillDisciplineDetails(row, name, tr, nameInput) {
      if (!name) return;
      const cleanVal = name.trim().toLowerCase();
      
      const matchedData = knownDisciplinesData[cleanVal];
      if (matchedData) {
        if (!row.code && matchedData.code) {
          row.code = matchedData.code;
          const codeEl = tr ? tr.querySelector(".code-col input") : null;
          if (codeEl) codeEl.value = matchedData.code;
        }
        row.credits = matchedData.credits || "";
        row.hours = matchedData.hours || "";
        row.lecture = matchedData.lecture || "";
        row.practice = matchedData.practice || "";
        row.lab = matchedData.lab || "";
        row.semesters = JSON.parse(JSON.stringify(matchedData.semesters || emptySemesters()));
      } else {
        const matchInCurrent = rows.find(r => r.type === "discipline" && r.id !== row.id && r.name && r.name.trim().toLowerCase() === cleanVal);
        if (matchInCurrent) {
          if (!row.code && matchInCurrent.code) {
            row.code = matchInCurrent.code;
            const codeEl = tr ? tr.querySelector(".code-col input") : null;
            if (codeEl) codeEl.value = matchInCurrent.code;
          }
          row.credits = matchInCurrent.credits || "";
          row.hours = matchInCurrent.hours || "";
          row.lecture = matchInCurrent.lecture || "";
          row.practice = matchInCurrent.practice || "";
          row.lab = matchInCurrent.lab || "";
          row.semesters = JSON.parse(JSON.stringify(matchInCurrent.semesters || emptySemesters()));
        }
      }

      const matchingTeachers = teachers.filter(t => (t.disciplines || []).map(d => String(d || "").trim().toLowerCase()).includes(cleanVal));
      if (matchingTeachers.length === 1) {
        row.teacherId = matchingTeachers[0].id;
        row.teacherName = matchingTeachers[0].fullName;
        const selEl = tr ? tr.querySelector(".teacher-select") : null;
        if (selEl) selEl.value = row.teacherId;
      }
    }

    async function loadTeachers() {
      try {
        const snap = await getDocs(query(collection(db, "users"), where("role", "in", ["teacher", "lab", "laborant", "лаборант", "admin"])));
        teachers = snap.docs.map(doc => {
          const data = doc.data();
          const fullName = data.fullName || data.name || data.displayName || "";
          return {
            id: doc.id,
            fullName,
            email: data.email || "",
            disciplines: data.disciplines || []
          };
        });
      } catch (err) {
        console.error("Error loading teachers inside CurriculumPlanPage:", err);
      }
    }

    async function init() {
      if ($("direction")) $("direction").value = directionRef.current;
      if ($("studyForm")) $("studyForm").value = studyFormRef.current;

      rows = defaultRows();
      renderBlocks();
      await loadTeachers();
      await loadAllKnownDisciplines();
      await loadYears();

      if ($("year")) {
        const sel = $("year");
        if (years.includes(yearRef.current)) {
          sel.value = yearRef.current;
        } else {
          yearRef.current = sel.value;
        }
      }

      await loadPlan();
      setStatus(text.statusReady);
      updateModeUI();
    }

    init();

    return () => {
      if (directionSelect) directionSelect.removeEventListener("change", handleDirectionChange);
      if (yearSelect)       yearSelect.removeEventListener("change", handleYearChange);
      if (studyFormSelect)  studyFormSelect.removeEventListener("change", handleStudyFormChange);
      if (!isReadOnly) {
        if (editBtn)     editBtn.removeEventListener("click",     enableEdit);
        if (saveBtn)     saveBtn.removeEventListener("click",     savePlan);
        if (addBlockBtn) addBlockBtn.removeEventListener("click", addCycle);
        if (addYearBtn)  addYearBtn.removeEventListener("click",  addYear);
      }
      if (excelBtn) excelBtn.removeEventListener("click", exportExcel);
    };
  }, [isReadOnly, isTeacher, isLab, language]);

  return (
    <div ref={rootRef} className="curriculum-body">
      <div className="plan-page" id="mainArea">

        <div className="top-panel">
          <div className="title-box">
            <h1>{text.pageTitle}</h1>
            <div className="meta">
              <label>
                {text.direction}
                <select id="direction">
                  <option value="ИВТ">ИВТ</option>
                  <option value="ПИЭ">ПИЭ</option>
                </select>
              </label>

              <label>
                {text.academicYear}
                <select id="year" defaultValue="2025-2026"></select>
              </label>

              {!isReadOnly && (
                <button type="button" className="btn-secondary small-action" id="addYearBtn">
                  <Plus size={13} style={{ marginRight: '4px' }} />
                  {text.addYear}
                </button>
              )}

              <label>
                {text.studyForm}
                <select id="studyForm">
                  <option value="Очная">{text.fullTime}</option>
                  <option value="Заочная">{text.partTime}</option>
                </select>
              </label>
            </div>
          </div>

          <div className="actions">
            <span id="statusText" className="status-inline-text edit-only" style={{ display: isTeacher ? 'none' : 'inline-block' }}>{text.statusReady}</span>
            {!isReadOnly && (
              <>
                <button type="button" className="btn-primary" id="editBtn">
                  <Pencil size={14} style={{ marginRight: '6px' }} />
                  {text.edit}
                </button>
                <button type="button" className="btn-primary"   id="saveBtn">
                  <Save size={14} style={{ marginRight: '6px' }} />
                  {text.save}
                </button>
                <button type="button" className="btn-secondary edit-only" id="addBlockBtn">
                  <Plus size={14} style={{ marginRight: '6px' }} />
                  {text.addBlock}
                </button>
              </>
            )}
            <button type="button" className="btn-secondary excel-btn" id="excelBtn">
              <FileSpreadsheet size={14} style={{ marginRight: '6px' }} />
              {text.exportExcel}
            </button>
          </div>
        </div>

        <div className="filter-tabs-bar" id="filterTabsBar"></div>

        <div className="blocks-container" id="blocksContainer"></div>

      </div>
    </div>
  );
}